import React from 'react';

// Msg #778 — Grouped action-tab bar for the top of the chat pane.
// Three columns: YOU (identity/account) · WORLD (map/leaderboard/admin
// tools) · SOCIAL (PM/friends/requests).  Each tab is a small pill so the
// bar stays compact and visually mirrors the classic 2006-era PHP-chat menu
// while giving the user a cleaner mental model than a flat "|"-separated list.

const groupWrap = {
  display: 'flex',
  gap: 4,
  alignItems: 'center',
  padding: '2px 8px',
  border: '1px solid #5b8c47',
  background: '#e9f3da',
  borderRadius: 6,
};

const groupLabel = {
  fontSize: 10,
  fontWeight: 'bold',
  letterSpacing: 1,
  color: '#2d6b1e',
  marginRight: 2,
  textTransform: 'uppercase',
};

const tabPill = (extra) => ({
  fontSize: 11,
  cursor: 'pointer',
  padding: '2px 8px',
  borderRadius: 4,
  color: '#1c3a1c',
  background: '#d6e8c5',
  border: '1px solid transparent',
  transition: 'background 120ms, border-color 120ms',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  ...(extra || {}),
});

const Pill = ({ label, onClick, testId, title, badge, children, danger }) => (
  <span
    className="tab-pill"
    data-testid={testId}
    title={title}
    onClick={onClick}
    style={tabPill(danger ? { color: '#7a1f1f', borderColor: '#a02020' } : {})}
    onMouseEnter={(e) => { e.currentTarget.style.background = '#c7dfa9'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = '#d6e8c5'; }}
  >
    {label}
    {children}
    {badge != null && badge > 0 && (
      <span
        data-testid={testId ? `${testId}-badge` : undefined}
        style={{
          marginLeft: 4,
          background: '#7a1f1f',
          color: '#fff3d0',
          padding: '1px 5px',
          borderRadius: 6,
          fontSize: 10,
          fontWeight: 'bold',
        }}
      >
        {badge}
      </span>
    )}
  </span>
);

export const TopTabsBar = ({
  onOpenPm, unreadPm,
  onOpenWorldMap, onOpenLeaderboard, onOpenQuestBoard,
  onOpenAddFriend, onOpenFriendInbox, pendingInboxCount,
  onOpenRegister, onOpenEditProfile, onOpenDelete,
  isAdmin, isMod, cloaked, onToggleCloak, onOpenPrivateRoomMonitor,
  // Msg #995 — SOCIAL: Friends List pill toggles the standalone FriendsListBox.
  friendsCount, friendsBoxOpen, onToggleFriendsBox,
  // Msg #995 — ADMIN: Admin Control toggles the right-sidebar admin panel;
  // Recap toggles the WhisperShadowConsole (bottom-right whisper feed).
  adminBoxOpen, onToggleAdminBox,
  privateRoomsAllowed, onTogglePrivateRoomsAllowed,
  whisperShadowOpen, onToggleWhisperShadow,
}) => {
  return (
    <div
      className="grouped-tabs-bar"
      data-testid="top-tabs-bar"
      style={{
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      {/* YOU */}
      <div style={groupWrap} data-testid="tabs-group-you">
        <span style={groupLabel}>You</span>
        <Pill
          testId="tab-edit-profile"
          label="Edit Profile"
          title="Edit your character profile"
          onClick={onOpenEditProfile}
        />
        <Pill
          testId="tab-register"
          label="Register"
          title="Save a permanent profile"
          onClick={onOpenRegister}
        />
        <Pill
          testId="tab-delete"
          label="Delete"
          title="Delete your registered profile"
          onClick={onOpenDelete}
          danger
        />
      </div>

      {/* WORLD */}
      <div style={groupWrap} data-testid="tabs-group-world">
        <span style={groupLabel}>World</span>
        <Pill
          testId="tab-world-map"
          label="🗺 World Map"
          title="Open the World Map of Mandala"
          onClick={onOpenWorldMap}
        />
        <Pill
          testId="tab-leaderboard"
          label="⚔ Leaderboard"
          title="Combat leaderboard — top damage dealers"
          onClick={onOpenLeaderboard}
        />
        <Pill
          testId="tab-quest-board"
          label="📜 Quests"
          title="Daily Quest Board — hunt, chat, gift for rewards"
          onClick={onOpenQuestBoard}
        />
      </div>

      {/* SOCIAL — Msg #995: FRIENDS LIST pill toggles standalone friends box. */}
      <div style={groupWrap} data-testid="tabs-group-social">
        <span style={groupLabel}>Social</span>
        <Pill
          testId="tab-pm"
          label="💬 PM"
          title="Open private messenger"
          onClick={onOpenPm}
          badge={unreadPm}
        />
        <Pill
          testId="tab-add-friend"
          label="+ Add Friend"
          title="Find any character and add them to your friends list"
          onClick={onOpenAddFriend}
        />
        {onToggleFriendsBox && (
          <span
            data-testid="tab-friends-list"
            className="tab-pill"
            onClick={onToggleFriendsBox}
            title="Toggle the Friends List box"
            style={tabPill(friendsBoxOpen
              ? { background: '#4a2d6b', color: '#f0e0ff', border: '1px solid #2a1a4a', fontWeight: 'bold' }
              : { background: '#6b4a8a', color: '#f0e0ff', border: '1px solid #3a2a5a', fontWeight: 'bold' })}
            onMouseEnter={(e) => { e.currentTarget.style.background = friendsBoxOpen ? '#3a1d5a' : '#5a3a7a'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = friendsBoxOpen ? '#4a2d6b' : '#6b4a8a'; }}
          >
            👥 FRIENDS LIST{friendsCount > 0 ? ` (${friendsCount})` : ''}
          </span>
        )}
        <Pill
          testId="tab-requests"
          label="🤝 Requests"
          title="Incoming friend requests"
          onClick={onOpenFriendInbox}
          badge={pendingInboxCount}
        />
      </div>

      {/* ADMIN — Msg #995: Admin Control / Cloaked / Private Rooms / Recap. */}
      {(isAdmin || isMod) && (
        <div
          style={{
            ...groupWrap,
            background: '#efe6c8',
            border: '1px solid #7a5a1a',
          }}
          data-testid="tabs-group-admin"
        >
          <span style={{ ...groupLabel, color: '#5a4a1d' }}>Admin</span>
          {onToggleAdminBox && (
            <span
              data-testid="tab-admin-control"
              className="tab-pill"
              onClick={onToggleAdminBox}
              title={adminBoxOpen ? 'Collapse admin control' : 'Expand admin control'}
              style={tabPill(adminBoxOpen
                ? { background: '#1c3a1c', color: '#ecfcd9', border: '1px solid #0a2a0a', fontWeight: 'bold' }
                : { background: '#7a1a1a', color: '#ffe1dc', border: '1px solid #3a0708', fontWeight: 'bold' })}
              onMouseEnter={(e) => { e.currentTarget.style.background = adminBoxOpen ? '#0a2a0a' : '#5a0f0f'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = adminBoxOpen ? '#1c3a1c' : '#7a1a1a'; }}
            >
              {adminBoxOpen ? '▲ ADMIN CONTROL' : '▼ ADMIN CONTROL'}
            </span>
          )}
          {onToggleCloak && (
            <span
              data-testid="tab-cloaked"
              className="tab-pill"
              onClick={() => onToggleCloak(!cloaked)}
              title={cloaked ? 'You are cloaked. Click to uncloak.' : 'Click to enter cloaked mode.'}
              style={tabPill(cloaked
                ? { background: '#1c3a3a', color: '#dff5f5', border: '1px solid #0e2222', fontWeight: 'bold' }
                : { background: '#c8c0a8', color: '#3a2b14', border: '1px solid #7a5a1a', fontWeight: 'bold' })}
              onMouseEnter={(e) => { e.currentTarget.style.background = cloaked ? '#0e2222' : '#b8b098'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = cloaked ? '#1c3a3a' : '#c8c0a8'; }}
            >
              {cloaked ? '🥷 CLOAKED' : '🥷 Cloaked'}
            </span>
          )}
          {onOpenPrivateRoomMonitor && (
            <Pill
              testId="tab-private-rooms"
              label="🕵 Private Rooms"
              title="Monitor all private rooms invisibly"
              onClick={onOpenPrivateRoomMonitor}
            />
          )}
          {onToggleWhisperShadow && (
            <span
              data-testid="tab-recap"
              className="tab-pill"
              onClick={onToggleWhisperShadow}
              title={whisperShadowOpen
                ? 'Hide the Whisper Shadow / Recap console'
                : 'Show the Whisper Shadow / Recap console'}
              style={tabPill(whisperShadowOpen
                ? { background: '#2d6b1e', color: '#f5f5e8', border: '1px solid #0f2a08', fontWeight: 'bold' }
                : { background: '#8fbf78', color: '#1c3a1c', border: '1px solid #2d6b1e', fontWeight: 'bold' })}
              onMouseEnter={(e) => { e.currentTarget.style.background = whisperShadowOpen ? '#1a5a10' : '#7fae68'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = whisperShadowOpen ? '#2d6b1e' : '#8fbf78'; }}
            >
              📼 RECAP
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default TopTabsBar;
